import React, { useEffect, useMemo, useState } from 'react';
import {
  SystemAdminDataService,
  PricingTierVersion,
  PublishTierPayload,
  TierImpact,
  formatPrice,
} from '../../services/systemAdminDataService';

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const tomorrow = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** A blank tier, for "New tier". Everything off by default — see the AI note. */
const emptyDraft = (): PublishTierPayload => ({
  code: '',
  effectiveFrom: tomorrow(),
  displayName: '',
  audience: null,
  maxChildren: null,
  // Full AI is unreleased, so a new tier must not quietly grant it.
  aiInsightsIncluded: false,
  monthlyPrice: null,
  annualPrice: null,
  currency: 'GBP',
  sortOrder: 0,
  isSellable: true,
  notes: null,
});

/**
 * System Admin → Pricing. The price book, which is data rather than a constant
 * in the marketing site — so this screen is the only place it is edited.
 *
 * Append-only: publishing a change to an existing tier INSERTS a new dated
 * version, it never rewrites one. Versions in force or past are immutable
 * because an invoice may already point at them; future-dated drafts stay
 * editable and discardable. The database enforces that, not this screen.
 */
const PricingTiers: React.FC = () => {
  const [versions, setVersions] = useState<PricingTierVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [draft, setDraft] = useState<PublishTierPayload | null>(null);
  const [draftIsNewTier, setDraftIsNewTier] = useState(false);
  const [impact, setImpact] = useState<TierImpact | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setVersions(await SystemAdminDataService.fetchPricing());
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load the price book.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // Group versions under their tier, current first, history after.
  const tiers = useMemo(() => {
    const byCode = new Map<string, PricingTierVersion[]>();
    for (const v of versions) {
      const list = byCode.get(v.code) ?? [];
      list.push(v);
      byCode.set(v.code, list);
    }
    return Array.from(byCode.entries())
      .map(([code, list]) => ({
        code,
        current: list.find(v => v.isCurrent) ?? null,
        drafts: list.filter(v => v.isDraft),
        past: list.filter(v => !v.isCurrent && !v.isDraft),
        sortOrder: list[0]?.sortOrder ?? 0,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
  }, [versions]);

  const openNewVersion = async (from: PricingTierVersion) => {
    setDraftIsNewTier(false);
    setSaveError(null);
    setDraft({
      code: from.code,
      effectiveFrom: tomorrow(),
      displayName: from.displayName,
      audience: from.audience,
      maxChildren: from.maxChildren,
      aiInsightsIncluded: from.aiInsightsIncluded,
      monthlyPrice: from.monthlyPrice,
      annualPrice: from.annualPrice,
      currency: from.currency,
      sortOrder: from.sortOrder,
      isSellable: from.isSellable,
      notes: null,
    });
    // A tier edit is a bulk edit — show who it lands on before it is saved.
    try {
      setImpact(await SystemAdminDataService.fetchTierImpact(from.code));
    } catch {
      setImpact(null);
    }
  };

  const openNewTier = () => {
    setDraftIsNewTier(true);
    setSaveError(null);
    setImpact(null);
    setDraft(emptyDraft());
  };

  const publish = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      await SystemAdminDataService.publishTierVersion(draft);
      setDraft(null);
      await load();
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'The version could not be published.');
    } finally {
      setSaving(false);
    }
  };

  const discardDraft = async (v: PricingTierVersion) => {
    try {
      await SystemAdminDataService.deleteTierDraft(v.code, v.effectiveFrom);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The draft could not be discarded.');
    }
  };

  const toggle = (code: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(code)) { next.delete(code); } else { next.add(code); }
      return next;
    });

  const set = <K extends keyof PublishTierPayload>(key: K, value: PublishTierPayload[K]) =>
    setDraft(d => (d ? { ...d, [key]: value } : d));

  return (
    <div className="sys-page">
      <div className="sys-page__head">
        <p className="sys-page__eyebrow">Platform</p>
        <h1 className="sys-page__title">Pricing</h1>
        <span className="sys-page__count">
          {loading ? 'Loading…' : `${tiers.length} ${tiers.length === 1 ? 'tier' : 'tiers'}`}
        </span>
      </div>

      <div className="pill-toolbar" role="toolbar" aria-label="Pricing actions">
        <span className="sys-field__note">
          Changes are published as a new dated version. Versions in force stay as they are.
        </span>
        <div className="pill-toolbar__spacer" />
        <button type="button" className="sys-back" onClick={openNewTier}>
          <i className="fas fa-plus" aria-hidden="true" />New tier
        </button>
        <button
          className="pill-toolbar__icon-btn ms-2"
          onClick={() => { setLoading(true); void load(); }}
          disabled={loading}
          title="Reload"
          aria-label="Reload the price book"
        >
          <i className="fas fa-sync-alt" style={loading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
        </button>
      </div>

      <div className="sys-page__body">
        {error && <div className="alert alert-danger">{error}</div>}

        {loading && versions.length === 0 ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : (
          <div className="sys-cards">
            {tiers.map(t => {
              const cur = t.current;
              const isOpen = expanded.has(t.code);
              return (
                <section key={t.code} className="ivory-panel sys-panel sys-panel--pad">
                  <div className="sys-panel__head">
                    <div>
                      <h2 className="sys-tier__name">
                        {cur?.displayName ?? t.code}
                        {cur && !cur.isSellable && <span className="sys-tag">retired</span>}
                      </h2>
                      {cur?.audience && <div className="sys-field__note">{cur.audience}</div>}
                    </div>
                    {cur && (
                      <button type="button" className="sys-back" onClick={() => openNewVersion(cur)}>
                        <i className="fas fa-plus" aria-hidden="true" />New version
                      </button>
                    )}
                  </div>

                  {cur ? (
                    <div className="sys-tier__facts">
                      <span className="sys-field__note">In force since {fmtDate(cur.effectiveFrom)}</span>
                      <span>
                        {cur.maxChildren == null ? 'Unlimited' : <>Up to <strong>{cur.maxChildren}</strong></>} children
                      </span>
                      <span>{formatPrice(cur.monthlyPrice, cur.currency)}<span className="sys-field__note">/mo</span></span>
                      <span>{formatPrice(cur.annualPrice, cur.currency)}<span className="sys-field__note">/yr</span></span>
                      <span>
                        Full AI:{' '}
                        {cur.aiInsightsIncluded
                          ? <span className="sys-state sys-state--on">included</span>
                          : <span className="sys-state sys-state--off">not included</span>}
                      </span>
                    </div>
                  ) : (
                    <div className="sys-field__note">No version in force yet.</div>
                  )}

                  {t.drafts.map(d => (
                    <div key={d.effectiveFrom} className="sys-notice mt-2">
                      <i className="fas fa-pen-ruler" aria-hidden="true" />
                      <span>
                        Scheduled for <strong>{fmtDate(d.effectiveFrom)}</strong>:{' '}
                        {d.maxChildren == null ? 'unlimited' : `up to ${d.maxChildren}`} children,{' '}
                        {formatPrice(d.monthlyPrice, d.currency)}/mo, full AI{' '}
                        {d.aiInsightsIncluded ? 'included' : 'not included'}.
                      </span>
                      <button type="button" className="sys-back ms-auto" onClick={() => discardDraft(d)}>
                        Discard
                      </button>
                    </div>
                  ))}

                  {t.past.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="sys-tier__toggle"
                        onClick={() => toggle(t.code)}
                        aria-expanded={isOpen}
                      >
                        <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'}`} aria-hidden="true" />
                        {t.past.length} earlier {t.past.length === 1 ? 'version' : 'versions'}
                      </button>
                      {isOpen && (
                        <div className="sys-table-scroll mt-2">
                          <table className="sys-table">
                            <thead>
                              <tr>
                                <th>From</th>
                                <th className="sys-table__num">Children</th>
                                <th className="sys-table__num">Monthly</th>
                                <th className="sys-table__num">Annual</th>
                                <th>Full AI</th>
                              </tr>
                            </thead>
                            <tbody>
                              {t.past.map(v => (
                                <tr key={v.effectiveFrom}>
                                  <td className="sys-table__muted">{fmtDate(v.effectiveFrom)}</td>
                                  <td className="sys-table__num">{v.maxChildren ?? '∞'}</td>
                                  <td className="sys-table__num">{formatPrice(v.monthlyPrice, v.currency)}</td>
                                  <td className="sys-table__num">{formatPrice(v.annualPrice, v.currency)}</td>
                                  <td className="sys-table__muted">{v.aiInsightsIncluded ? 'Included' : 'Not included'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {draft && (
        <div className="modal show d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(13,77,26,0.35)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content ivory-panel">
              <div className="modal-header">
                <h5 className="modal-title">
                  {draftIsNewTier ? 'New tier' : `New version of ${draft.displayName || draft.code}`}
                </h5>
                <button type="button" className="btn-close" onClick={() => setDraft(null)} aria-label="Close" disabled={saving} />
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  {draftIsNewTier && (
                    <div className="col-sm-6">
                      <label className="form-label" htmlFor="pt-code">Code</label>
                      <input
                        id="pt-code"
                        className="form-control"
                        value={draft.code}
                        onChange={e => set('code', e.target.value.toLowerCase())}
                        placeholder="e.g. large"
                        disabled={saving}
                      />
                      <div className="form-text">Permanent once an organisation is on it.</div>
                    </div>
                  )}
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="pt-name">Name</label>
                    <input
                      id="pt-name"
                      className="form-control"
                      value={draft.displayName}
                      onChange={e => set('displayName', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="pt-from">Effective from</label>
                    <input
                      id="pt-from"
                      type="date"
                      className="form-control"
                      value={draft.effectiveFrom}
                      min={draftIsNewTier ? undefined : tomorrow()}
                      onChange={e => set('effectiveFrom', e.target.value)}
                      disabled={saving}
                    />
                    {!draftIsNewTier && (
                      <div className="form-text">
                        Must be in the future — versions in force may already have been invoiced.
                      </div>
                    )}
                  </div>
                  <div className="col-12">
                    <label className="form-label" htmlFor="pt-audience">Who it&rsquo;s for</label>
                    <input
                      id="pt-audience"
                      className="form-control"
                      value={draft.audience ?? ''}
                      onChange={e => set('audience', e.target.value || null)}
                      placeholder="e.g. Small settings & SEND units"
                      disabled={saving}
                    />
                  </div>

                  <div className="col-sm-4">
                    <label className="form-label" htmlFor="pt-children">Children permitted</label>
                    <input
                      id="pt-children"
                      type="number"
                      min={1}
                      className="form-control"
                      value={draft.maxChildren ?? ''}
                      onChange={e => set('maxChildren', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      placeholder="Unlimited"
                      disabled={saving}
                    />
                    <div className="form-text">Leave empty for unlimited.</div>
                  </div>
                  <div className="col-sm-4">
                    <label className="form-label" htmlFor="pt-monthly">Monthly price</label>
                    <input
                      id="pt-monthly"
                      type="number"
                      min={0}
                      step="0.01"
                      className="form-control"
                      value={draft.monthlyPrice ?? ''}
                      onChange={e => set('monthlyPrice', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="POA"
                      disabled={saving}
                    />
                  </div>
                  <div className="col-sm-4">
                    <label className="form-label" htmlFor="pt-annual">Annual price</label>
                    <input
                      id="pt-annual"
                      type="number"
                      min={0}
                      step="0.01"
                      className="form-control"
                      value={draft.annualPrice ?? ''}
                      onChange={e => set('annualPrice', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="POA"
                      disabled={saving}
                    />
                    {draft.monthlyPrice != null && draft.annualPrice == null && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 mt-1"
                        onClick={() => set('annualPrice', (draft.monthlyPrice as number) * 10)}
                      >
                        Use 10× monthly (two months free)
                      </button>
                    )}
                  </div>

                  <div className="col-12">
                    <div className="form-check">
                      <input
                        id="pt-ai"
                        className="form-check-input"
                        type="checkbox"
                        checked={draft.aiInsightsIncluded}
                        onChange={e => set('aiInsightsIncluded', e.target.checked)}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="pt-ai">
                        Full AI enrichment and Insights included
                      </label>
                    </div>
                    <div className="form-text">
                      Every organisation on this tier picks this up on the effective
                      date, unless it has a per-organisation override.
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-check">
                      <input
                        id="pt-sellable"
                        className="form-check-input"
                        type="checkbox"
                        checked={draft.isSellable}
                        onChange={e => set('isSellable', e.target.checked)}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="pt-sellable">Still on sale</label>
                    </div>
                    <div className="form-text">
                      Unticking hides it from the tier picker. Organisations already
                      on it keep it.
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="pt-order">Display order</label>
                    <input
                      id="pt-order"
                      type="number"
                      className="form-control"
                      value={draft.sortOrder}
                      onChange={e => set('sortOrder', parseInt(e.target.value, 10) || 0)}
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* A tier edit is a bulk edit. Say who it lands on. */}
                {impact && impact.organisations > 0 && (
                  <div className="sys-notice mt-3">
                    <i className="fas fa-users" aria-hidden="true" />
                    <span>
                      <strong>{impact.organisations}</strong>{' '}
                      {impact.organisations === 1 ? 'organisation is' : 'organisations are'} on this tier
                      {impact.overNewLimit > 0 && (
                        <>, and <strong>{impact.overNewLimit}</strong> {impact.overNewLimit === 1 ? 'is' : 'are'} already over its limit</>
                      )}.
                    </span>
                  </div>
                )}

                {saveError && <div className="alert alert-danger mt-3 mb-0">{saveError}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDraft(null)} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={publish}
                  disabled={saving || !draft.code.trim() || !draft.displayName.trim()}
                >
                  {saving ? 'Publishing…' : 'Publish version'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingTiers;
